import { useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { ApiError } from "@/core/api/apiTypes";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { validDateKey } from "@/shared/utils/calendarDate";
import { bookingPrefillInstant } from "@/features/appointments/utils/bookingTime";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Switch } from "@/shared/components/shadcn/Switch";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import type { DialogDismissReason } from "@/shared/components/ui/FullScreenDialog";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate, getBusinessDateKey } from "@/shared/utils/formatters";
import { useCalendarEventConflicts, useCreateCalendarEvent, useUpdateCalendarEvent } from "../hooks/calendarEventQueries";
import { useCalendarEventOperation } from "../hooks/useCalendarEventOperation";
import { calendarEventApiService } from "../services/calendarEventApiService";
import type { CalendarEvent, CalendarEventConflictPreview, CalendarEventKind } from "../types/calendarEventTypes";
import { eventLocalValue, eventRange } from "../utils/eventTime";
import { CalendarEventDateTime } from "./CalendarEventDateTime";
import { CalendarEventDateTimePicker, type CalendarEventTimeDraft } from "./CalendarEventDateTimePicker";

interface EventDraft extends CalendarEventTimeDraft {
  title: string;
  notes: string;
  blocksAvailability: boolean;
}

export function CalendarEventForm({ kind, event: initialEvent }: { kind: CalendarEventKind; event?: CalendarEvent }) {
  const [event] = useState(initialEvent);
  const [endConfirmed, setEndConfirmed] = useState(!!initialEvent);
  const { business, businessId, locations } = useCurrentBusiness();
  const { goToCalendarEventDetails } = useAppNavigation();
  const [params, setParams] = useSearchParams();
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const pickerOpen = params.get("picker") === "date";
  function setPicker(open: boolean, reason?: DialogDismissReason) {
    if (reason === "navigate") return;
    const historyState = routeLocation.state as { calendarEventPicker?: boolean } | null;
    if (!open && historyState?.calendarEventPicker) { navigate(-1); return; }
    const search = new URLSearchParams(params);
    if (open) search.set("picker", "date");
    else search.delete("picker");
    setParams(search, { replace: !open, state: open ? { calendarEventPicker: true } : null });
  }
  const timezone = event?.timezone ?? business!.timezone;
  const minimumStartDay = event ? undefined : getBusinessDateKey(new Date(), timezone);
  const [prefill] = useState(() => {
    const startAt = bookingPrefillInstant(params.get("startAt") ?? undefined);
    return startAt ? { startAt, endAt: new Date(startAt.getTime() + 3_600_000), isAllDay: false } : undefined;
  });
  const [draft, setDraft] = useState<EventDraft>(() => {
    const date = params.get("date");
    const day = validDateKey(date) ? date : getBusinessDateKey(new Date(), timezone);
    const time = params.get("time");
    const start = `${day}T${time && /^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? time : "09:00"}`;
    const end = new Date(new Date(`${start}:00Z`).getTime() + 3_600_000).toISOString().slice(0, 16);
    return {
      title: event?.title ?? "", notes: event?.notes ?? "",
      start: event ? (event.isAllDay ? getBusinessDateKey(event.startAt, timezone) : eventLocalValue(event.startAt, timezone)) : prefill ? eventLocalValue(prefill.startAt, timezone) : start,
      end: event ? (event.isAllDay ? getBusinessDateKey(new Date(event.endAt.getTime() - 1), timezone) : eventLocalValue(event.endAt, timezone)) : prefill ? eventLocalValue(prefill.endAt, timezone) : end,
      isAllDay: event?.isAllDay ?? false,
      blocksAvailability: event?.blocksAvailability ?? true,
    };
  });
  const create = useCreateCalendarEvent();
  const update = useUpdateCalendarEvent(event?.id ?? "");
  const conflicts = useCalendarEventConflicts();
  const [review, setReview] = useState<{ preview: CalendarEventConflictPreview; startAt: Date; endAt: Date }>();
  const [error, setError] = useState<string>();
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const captureOperation = useCalendarEventOperation();
  const revision = useRef(0);
  const busy = saving || create.isPending || update.isPending || conflicts.isPending;

  function change(next: Partial<EventDraft>) {
    revision.current += 1;
    setDraft((current) => ({ ...current, ...next }));
    setReview(undefined);
    setError(undefined);
  }

  async function reviewDraft(currentRevision = revision.current, isCurrent = captureOperation()) {
    if (!isCurrent() || currentRevision !== revision.current) return false;
    setError(undefined);
    try {
      if (kind === "personal" && !draft.title.trim()) throw new Error("Enter a title for your personal event.");
      const interval = eventRange(draft.start, draft.end, draft.isAllDay, timezone, event ?? prefill, minimumStartDay);
      const unchangedOccupancy = event && interval.startAt.getTime() === event.startAt.getTime()
        && interval.endAt.getTime() === event.endAt.getTime() && draft.blocksAvailability === event.blocksAvailability;
      const preview: CalendarEventConflictPreview = unchangedOccupancy
        ? { conflicts: [], conflictToken: null }
        : await conflicts.mutateAsync({ ...interval, blocksAvailability: draft.blocksAvailability, excludeEventId: event?.id });
      if (!isCurrent() || currentRevision !== revision.current) return false;
      setReview({ ...interval, preview });
      return true;
    } catch (cause) {
      if (isCurrent() && currentRevision === revision.current) setError(cause instanceof Error ? cause.message : "The event could not be reviewed.");
      return false;
    }
  }

  async function save() {
    if (!review || !businessId) {
      setError("Review the event in the selected business before saving.");
      return;
    }
    const isCurrent = captureOperation();
    const currentRevision = revision.current;
    setSaving(true);
    setError(undefined);
    try {
      const data = {
        title: draft.title.trim() || null, notes: draft.notes.trim() || null,
        startAt: review.startAt, endAt: review.endAt, isAllDay: draft.isAllDay,
        blocksAvailability: kind === "blocked" || draft.blocksAvailability,
        conflictToken: review.preview.conflictToken ?? undefined,
      };
      const saved = event
        ? await update.mutateAsync({ ...data, expectedVersion: event.version })
        : await create.mutateAsync({ ...data, businessId, kind });
      if (isCurrent()) goToCalendarEventDetails(saved.id, true);
    } catch (cause) {
      if (!isCurrent() || currentRevision !== revision.current) return;
      if (cause instanceof ApiError && cause.Status === 409) {
        setReview(undefined);
        try {
          if (event) {
            const latest = await calendarEventApiService.getById(businessId, event.id);
            if (!isCurrent() || currentRevision !== revision.current) return;
            if (latest.version !== event.version) {
              setStale(true);
              setError("This event changed elsewhere. Your draft is preserved. Reopen the latest event before editing again.");
              return;
            }
          }
          if (await reviewDraft(currentRevision, isCurrent)) {
            setError("Appointments changed. Review the refreshed conflicts before saving again. Your draft is preserved.");
          }
        } catch (refreshError) {
          if (isCurrent()) setError(refreshError instanceof Error ? refreshError.message : "Could not refresh conflicts. Your draft is preserved.");
        }
      } else {
        setError(cause instanceof Error ? cause.message : "The event could not be saved.");
      }
    } finally {
      if (isCurrent()) setSaving(false);
    }
  }

  const title = kind === "personal" ? "Personal event" : "Blocked time";
  let interval: { startAt: Date; endAt: Date } | undefined;
  let intervalError: string | undefined;
  try {
    interval = eventRange(draft.start, draft.end, draft.isAllDay, timezone, event ?? prefill, minimumStartDay);
  } catch (cause) {
    intervalError = cause instanceof Error ? cause.message : "Choose a valid date and time.";
  }
  return (
    <>
      <FormPage
        header={<PageHeader title={event ? `Edit ${title.toLowerCase()}` : title} />}
        contentClassName="space-y-6"
        onSubmit={(submit) => { submit.preventDefault(); void reviewDraft(); }}
        actions={<Button type="submit" disabled={busy || stale} className="w-full">{busy ? "Please wait..." : "Review event"}</Button>}
      >
        <fieldset disabled={busy || stale} className="mx-auto w-full max-w-2xl space-y-6">
          <Section title="Event">
            <FormField id="event-title" label="Title" required={kind === "personal"} optional={kind === "blocked"}>
              <Input value={draft.title} maxLength={200} onChange={(e) => change({ title: e.target.value })} />
            </FormField>
          </Section>
          <Section title="When">
            <InfoRow
              title="Date & time"
              ariaLabel="Date & time"
              caption={interval ? <CalendarEventDateTime {...interval} isAllDay={draft.isAllDay} timezone={timezone} /> : "Choose a valid date and time"}
              icon={<CalendarDays className="text-primary" />}
              onClick={() => setPicker(true)}
              className="rounded-[20px]"
            />
            {intervalError && <p role="alert" className="text-sm text-destructive">{intervalError}</p>}
          </Section>
          <Section title="Availability">
            {kind === "personal"
              ? <InfoRow title="Show as free" subtitle="Visible on the calendar without blocking new bookings." actions={<Switch aria-label="Show as free" checked={!draft.blocksAvailability} disabled={busy} onCheckedChange={(checked) => change({ blocksAvailability: !checked })} />} />
              : <InfoRow title="Busy" subtitle="Blocks new bookings during this time." />}
          </Section>
          <Section title={<label htmlFor="event-notes" className="flex flex-wrap items-baseline gap-1">Notes <span className="text-xs font-normal text-muted-foreground">Optional</span></label>}>
            <TextArea id="event-notes" className="rounded-[20px]" rows={4} value={draft.notes} maxLength={2000} onChange={(e) => change({ notes: e.target.value })} />
          </Section>
        </fieldset>
        {error && !review && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {stale && event && <Button type="button" variant="outline" onClick={() => goToCalendarEventDetails(event.id, true)}>Reopen latest event</Button>}
      </FormPage>
      <CalendarEventDateTimePicker
        open={pickerOpen && !busy && !stale}
        value={{ start: draft.start, end: draft.end, isAllDay: draft.isAllDay }}
        endConfirmed={endConfirmed}
        timezone={timezone}
        original={event ?? prefill}
        onApply={(value) => { setEndConfirmed(true); change(value); }}
        onClose={(reason) => setPicker(false, reason)}
      />
      {review && <ConfirmationDialog
        open
        title="Review event"
        onOpenChange={(open) => { if (!open && !busy) { revision.current += 1; setReview(undefined); } }}
        confirmLabel="Save event"
        dismissLabel="Keep editing"
        pending={busy}
        error={error}
        onConfirm={() => void save()}
        description={
          <span className="mt-2 block divide-y divide-border rounded-2xl border border-border bg-muted/30 px-4 text-sm">
            <span role="group" aria-label="Event" className="block space-y-1 py-3">
              <span className="block text-xs font-medium">{title}</span>
              <span className="block break-words text-foreground">{draft.title.trim() || title}</span>
            </span>
            <span role="group" aria-label="Date & time" className="block space-y-1 py-3">
              <span className="block text-xs font-medium">Date & time</span>
              <span className="block text-foreground"><CalendarEventDateTime startAt={review.startAt} endAt={review.endAt} isAllDay={draft.isAllDay} timezone={timezone} /></span>
            </span>
            <span role="group" aria-label="Availability" className="block space-y-1 py-3">
              <span className="block text-xs font-medium">Availability</span>
              <span className="block text-foreground">{draft.blocksAvailability ? "Busy" : "Free"}</span>
            </span>
            {draft.notes.trim() && <span role="group" aria-label="Notes" className="block space-y-1 py-3">
              <span className="block text-xs font-medium">Notes</span>
              <span className="block whitespace-pre-wrap break-words text-foreground">{draft.notes.trim()}</span>
            </span>}
            {review.preview.conflicts.length > 0 && <span role="group" aria-label="Overlapping appointments" className="block space-y-3 py-3">
              <span className="block text-xs font-medium">Overlapping appointments</span>
              <span className="block text-foreground">These appointments will remain booked. Save only if you want to keep them and add this busy event.</span>
              {review.preview.conflicts.map((conflict) => <span key={conflict.appointmentId} className="block space-y-1 break-words">
                <span className="block text-foreground">{formatBusinessDate(conflict.startAt, timezone, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                <span className="block text-xs">{`Ends ${formatBusinessDate(conflict.endAt, timezone, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} - ${conflict.status} - ${conflict.locationId ? locations.find((location) => location.id === conflict.locationId)?.name ?? `Location ${conflict.locationId}` : "No location"}`}</span>
              </span>)}
            </span>}
          </span>
        }
      />}
    </>
  );
}
